import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../hooks/auth";
import { wrapperRequests } from "../../services/api";
import { Input } from "../../components/New/Input";
import { Button } from "../../components/New/Button";
import { LanguageSelector } from "../../components/LanguageSelector";
import * as S from "./styles";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { ToastPosition, toast } from "react-toastify";
import * as z from "zod";
import { routesURL } from "../../services/routesUrl";
import useLanguageSelector from "../../hooks/useLanguageSelector";

export function Profile() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { handleLoginLanguage } = useLanguageSelector();

  const schema = z.object({
    pin: z.union([
      z.string().length(4, { message: t("pinMustHave4Numbers") }),
      z.string().min(4, { message: t("pinMustHave4Numbers") }),
    ]),
    language: z.string(),
  });

  type UpdateUserSchema = z.infer<typeof schema>;

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<UpdateUserSchema>({
    resolver: zodResolver(schema),
    reValidateMode: "onSubmit",
  });

  const [loadings, setLoadings] = useState({
    updatePIN: false,
    updateLanguage: false,
  });
  const [updateErrorMessage, setUpdateErrorMessage] = useState("");
  const [languageErrorMessage, setLanguageErrorMessage] = useState("");

  const separateName = user.user.name.split(" ");

  const toastify = (message: string, position: ToastPosition) => {
    toast.success(message, {
      position,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  };

  const updateLoadingState = (key: string, value: boolean) => {
    setLoadings((state) => ({
      ...state,
      [key]: value,
    }));
  };

  const handleUpdatePIN = async (data: UpdateUserSchema) => {
    updateLoadingState("updatePIN", true);
    try {
      await wrapperRequests(
        routesURL.settings.users.editUser(user.user.id),
        "PUT",
        { data },
      );

      toastify(t("userEditedSuccessfully"), "top-center");
      setUpdateErrorMessage("");
    } catch (error: any) {
      console.error("Erro ao atualizar PIN:", error);
      setUpdateErrorMessage(
        error.response?.data?.message || t("duplicatedPin"),
      );
      throw Error(String(error));
    } finally {
      updateLoadingState("updatePIN", false);
    }
  };

  const handleUpdateLanguage = async (data: UpdateUserSchema) => {
    updateLoadingState("updateLanguage", true);
    try {
      await wrapperRequests(
        routesURL.settings.users.editUser(user.user.id),
        "PUT",
        { data },
      );

      handleLoginLanguage(data.language);
      toastify(t("userEditedSuccessfully"), "top-center");
      setLanguageErrorMessage("");
    } catch (error: any) {
      setLanguageErrorMessage(error.response?.data?.message);
      throw Error(String(error));
    } finally {
      updateLoadingState("updateLanguage", false);
    }
  };

  return (
    <S.Container>
      <S.Content>
        <S.PersonalInformation>
          <h2>{t("personalInformation")}</h2>
          <S.UserDataContainer>
            <Input
              title={t("firstName")}
              type="text"
              defaultValue={separateName[0]}
              disabled
            />
            <Input
              title={t("lastName")}
              type="text"
              defaultValue={separateName[1]}
              disabled
            />
            <Input
              title={t("email")}
              defaultValue={user.user.email}
              type="email"
              disabled
            />
            <Input title={t("birthDate")} disabled />
            <Input title={t("phoneNumber")} disabled />
          </S.UserDataContainer>
        </S.PersonalInformation>

        <S.LoginSecurity>
          <h2>{t("loginInformation")}</h2>
          <S.UpdatePasswordContainer>
            <Input title={t("currentPassword")} type="password" disabled />

            <Input title={t("newPassword")} type="password" disabled />
            <S.WrapperPin>
              <Input
                title={t("pin")}
                type="password"
                errorMessage={errors.pin?.message || updateErrorMessage}
                {...register("pin")}
              />
              {!errors.pin?.message && <span>{t("pinMustHave4Numbers")}</span>}
            </S.WrapperPin>
            <S.WrapperChangePassword hasError={!!updateErrorMessage}>
              <form
                className="form_update_pin"
                onSubmit={handleSubmit(handleUpdatePIN)}
              >
                <Button
                  title={t("save")}
                  onClick={handleSubmit(handleUpdatePIN)}
                  isLoading={loadings.updatePIN}
                  disabled={loadings.updatePIN}
                >
                  {t("save")}
                </Button>
              </form>
            </S.WrapperChangePassword>
          </S.UpdatePasswordContainer>
        </S.LoginSecurity>

        <S.LanguagePreferences>
          <LanguageSelector
            register={register("language")}
            defaultValue={user.user.language}
            error={languageErrorMessage}
            width="20rem"
          />

          <Button
            title={t("save")}
            onClick={handleSubmit(handleUpdateLanguage)}
            isLoading={loadings.updateLanguage}
            disabled={loadings.updateLanguage}
          >
            {t("save")}
          </Button>
        </S.LanguagePreferences>
      </S.Content>
    </S.Container>
  );
}
